import { create } from "zustand";

import { initialSessionState, storeSellerId } from "../seller-session";

// UI / session state for the talent SPA. Server state (the directory, a talent's
// thread list, a thread's detail) lives in the TanStack Query cache — see
// lib/queries/seller.ts — and the store NEVER holds a copy of it. This mirrors
// web's workspace-store split (server state in Query, UI/session in Zustand).
//
// `activeSellerId` is dual-use, exactly like web's `currentProjectId`: a component
// reads it via a selector to KEY the queries (switching talent re-keys the reads),
// and the Pusher dispatcher reads it via `getState()` to build the cache keys it
// writes — the two never drift because they read the same field.
interface SellerStore {
  /** The simulated talent's id. "" = no identity → the directory picker. */
  activeSellerId: string;
  /** The thread open in the chat pane (null = none). */
  selectedThreadId: string | null;
  /** Tester feedback toggle (red "Debug" pill). When ON, a floating note box
   *  lets the tester log debug notes (admin "Debug Notes" tab) from any view —
   *  the talent-side twin of the buyer app's toggle. Global tester tooling,
   *  persisted to localStorage so it survives a reload; NOT tied to a talent. */
  debugMode: boolean;

  /** Switch identity: persist it and clear the open thread (one atomic action). */
  setActiveSeller: (sellerId: string) => void;
  /** Open a specific talent's thread directly (the project-first pick): set the
   *  identity AND the open thread atomically, landing straight in the chat. */
  openSellerThread: (sellerId: string, threadId: string) => void;
  /** Clear identity → back to the picker (the "Browse all talents" path). */
  clearActiveSeller: () => void;
  setSelectedThread: (threadId: string | null) => void;
  setDebugMode: (on: boolean) => void;
}

// Debug-mode persistence. A GLOBAL tester setting (not per-talent), so it lives
// in localStorage — survives reloads, seeded into the store on creation. Same
// key the buyer app uses (a separate origin, so the stores are independent).
const DEBUG_MODE_KEY = "recruiter_debug_mode";

function getStoredDebugMode(): boolean {
  try {
    return window.localStorage?.getItem(DEBUG_MODE_KEY) === "1";
  } catch {
    return false;
  }
}

function storeDebugMode(on: boolean): void {
  try {
    if (on) window.localStorage?.setItem(DEBUG_MODE_KEY, "1");
    else window.localStorage?.removeItem(DEBUG_MODE_KEY);
  } catch {
    /* localStorage unavailable (private mode) — debug mode is best-effort. */
  }
}

// Boot state is URL-first (deep links win over the persisted identity) — see
// initialSessionState. Module-scope, runs once; App.tsx keeps URL and store in
// sync from here on (lib/route-url.ts).
const boot = initialSessionState();

export const useSellerStore = create<SellerStore>((set) => ({
  activeSellerId: boot.sellerId,
  selectedThreadId: boot.threadId,
  debugMode: getStoredDebugMode(),

  setActiveSeller: (sellerId) => {
    // Persist inline (mirrors web's syncWorkspace calling storeActiveProjectId) so a
    // reload restores the same simulated talent — no separate effect to order.
    storeSellerId(sellerId);
    set({ activeSellerId: sellerId, selectedThreadId: null });
  },
  openSellerThread: (sellerId, threadId) => {
    // Persist the identity (same as setActiveSeller) but keep the chosen thread
    // open so a project-first pick lands directly in the conversation.
    storeSellerId(sellerId);
    set({ activeSellerId: sellerId, selectedThreadId: threadId });
  },
  clearActiveSeller: () => {
    storeSellerId("");
    set({ activeSellerId: "", selectedThreadId: null });
  },
  setSelectedThread: (selectedThreadId) => set({ selectedThreadId }),
  setDebugMode: (on) => {
    storeDebugMode(on);
    set({ debugMode: on });
  },
}));
